//  This is a genericRepository to make GET, POST, DELETE HTTP method using axios

import axios from "./axiosConfig";
import type {HeaderProps} from "./axiosType";
import {encryptAPI, decryptAPI} from "../commonUtils/crypto";
import {isValidObject} from "commonUtils/utils";

export const setHeader = <T>(headers: T) => {
  const defaultHeaders = {
    Accept: "text/plain",
    "Access-Control-Allow-Origin": "*",
    "Content-Type": "application/json",
  };
  return {...defaultHeaders, ...headers};
};

const encryptURL = (url: string) => {
  const urlValues = url.split("?");
  if (urlValues.length > 1 && urlValues[1].length > 0) {
    url = urlValues[0] + "?" + encryptAPI(urlValues[1]);
  }
  return url;
};

export const getRequest = async <T>({
  url,
  headers,
  timeout,
  params,
}: {
  url: string;
  headers?: HeaderProps;
  timeout?: number;
  params?: T;
}) => {
  try {
    const response = await axios.get(encryptURL(url), {
      headers,
      timeout: timeout ?? 30000,
      params,
    });
    return decryptAPI(response.data);
  } catch (error: any) {
    if (isValidObject(error?.response?.data)) {
      error.response.data = decryptAPI(error.response.data);
    }
    return await Promise.reject(error);
  }
};

export const postRequest = async <T>({
  url,
  timeout,
  params,
  headers,
}: {
  url: string;
  timeout?: number;
  data?: T;
  params?: T;
  headers: HeaderProps;
}) => {
  try {
    const response = await axios.post(encryptURL(url), encryptAPI(params), {
      timeout: timeout ?? 30000,
      headers,
    });
    return decryptAPI(response.data);
  } catch (error: any) {
    if (isValidObject(error?.response?.data)) {
      error.response.data = decryptAPI(error.response.data);
    }
    return await Promise.reject(error);
  }
};
export const postDownloadRequest = async <T>({
  url,
  timeout,
  params,
  headers,
  responseType,
}: {
  url: string;
  timeout?: number;
  data?: T;
  params?: T;
  headers: HeaderProps;
  responseType: any;
}) => {
  try {
    const response = await axios.post(encryptURL(url), encryptAPI(params), {
      timeout: timeout ?? 30000,
      headers,
      responseType,
    });
    return response.data; // Response type is blob so no decryption needed
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const postFormDataRequest = async <T>({
  url,
  timeout,
  params,
  headers,
}: {
  url: string;
  timeout?: number;
  params?: T;
  headers?: HeaderProps;
}) => {
  try {
    const formData = new FormData();

    let file: File | undefined;
    const restParams: Record<string, any> = {};

    for (const key in params) {
      if (key === "file" || key === "iformFile") {
        const maybeFile = params[key];
        if (maybeFile instanceof File) {
          file = maybeFile;
        }
      } else {
        restParams[key] = params[key];
      }
    }

    formData.append("json", encryptAPI(JSON.stringify(restParams)));

    if (file) {
      formData.append("file", file);
    }

    const {["Content-Type"]: _, ...safeHeaders} = headers ?? {};

    const response = await axios.post(encryptURL(url), formData, {
      timeout: timeout ?? 3000000,
      headers: {
        ...safeHeaders,
      },
    });

    return decryptAPI(response.data);
  } catch (error: any) {
    if (isValidObject(error?.response?.data)) {
      error.response.data = decryptAPI(error.response.data);
    }
    return await Promise.reject(error);
  }
};

export const patchRequest = async <T>({
  url,
  timeout,
}: {
  url: string;
  timeout?: number;
  data: T;
  params: T;
}) => {
  try {
    const response = await axios.patch(encryptURL(url), {
      timeout: timeout ?? 30000,
    });
    return decryptAPI(response.data);
  } catch (error: any) {
    if (isValidObject(error?.response?.data)) {
      error.response.data = decryptAPI(error.response.data);
    }
    return await Promise.reject(error);
  }
};

export const patchFormDataRequest = async <T>({
  url,
  timeout,
}: {
  url: string;
  timeout?: number;
  data: T;
  params: T;
}) => {
  try {
    const response = await axios.patch(
      encryptURL(url),
      {timeout: timeout ?? 30000},
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return decryptAPI(response.data);
  } catch (error: any) {
    if (isValidObject(error?.response?.data)) {
      error.response.data = decryptAPI(error.response.data);
    }
    return await Promise.reject(error);
  }
};

export const putRequest = async <T>({
  url,
  timeout,
  params,
  headers,
}: {
  url: string;
  headers?: HeaderProps;
  timeout?: number;
  params?: T;
}) => {
  try {
    const response = await axios.post(encryptURL(url), encryptAPI(params), {
      timeout: timeout ?? 30000,
      headers,
    });
    return decryptAPI(response.data);
  } catch (error: any) {
    if (isValidObject(error?.response?.data)) {
      error.response.data = decryptAPI(error.response.data);
    }
    return await Promise.reject(error);
  }
};

export const deleteRequest = async <T>({
  url,
  timeout,
}: {
  url: string;
  timeout?: number;
  params: T;
}) => {
  try {
    const response = await axios.delete(encryptURL(url), {
      timeout: timeout ?? 30000,
    });
    return decryptAPI(response.data);
  } catch (error: any) {
    if (isValidObject(error?.response?.data)) {
      error.response.data = decryptAPI(error.response.data);
    }
    return await Promise.reject(error);
  }
};
